import { useEffect, useState } from 'react';
import { PageItem, strippedInputObj } from '../Interfaces/TestCaseInterface';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { TestSuiteEditor } from '../Components/TestSuiteEditor';
import TestCaseEditor from '../Components/TestCaseEditor';
import { TestSuite } from '../Interfaces/TestSuiteInteface';
import { TestSidebar } from '../Components/TestSidebar';
import Box from '@mui/material/Box';
import { ToastNotification } from '../Components/ToastNotification';
import { AddNewTestItem } from '../Components/AddNewTestItem';
import { ParsedXMLData, parseXMLPolicy } from '../B2XMLParsing/ParseXMLPolicy';
import { ErrorReturn, isErrorReturn } from '../Interfaces/UtilityInterface';
import { TestNavbar } from '../Components/TestNavbar';

export const TestAutomation = () => {
    const [tests, setTests] = useState<Record<string, Array<Array<strippedInputObj>>>>({});
    const [testSuites, setTestSuites] = useState<Record<string, TestSuite>>({});
    const [showNewSuite, setShowNewSuite] = useState(false);
    const [showNewTest, setShowNewTest] = useState(false);
    const [selectedXMLFile, setSelectedXMLFile] = useState<File | null>(null);
    // All user interactable policy variables
    const [policyVariables, setPolicyVariables] = useState<Record<string, PageItem>>({});
    // Page name and a list of the names of all userinteractable policy variables (actual data of variable is pulled from policyVariables)
    const [pageTemplates, setPageTemplates] = useState<Record<string, Array<string>>>({});
    const [pageNames, setPageNames] = useState<Array<string>>([]);
    const [userJournies, setUserJournies] = useState<Record<string, Array<string>>>({});
    let navigate = useNavigate();
    const [showToast, setShowToast] = useState(false);
    const [toastContents, setToastContents] = useState<{ header: string, body: string, severity?: 'error' | 'info' | 'success' | 'warning' }>({ header: '', body: '' });


    useEffect(() => {
        if (toastContents.header !== '' && toastContents.body !== '') {
            setShowToast(true);
        }
    }, [toastContents])

    useEffect(() => {
        if (showToast === false)
            setToastContents({ header: '', body: '' });
    }, [showToast])

    const downloadTestCase = (testName: string) => {
        let res = tests[testName];
        let dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(res));
        let downloadAnchorNode = document.createElement('a');
        downloadAnchorNode.setAttribute("href", dataStr);
        downloadAnchorNode.setAttribute("download", testName + ".json");
        document.body.appendChild(downloadAnchorNode); // required for firefox
        downloadAnchorNode.click();
        downloadAnchorNode.remove();
    }

    /*********************************************************************************************
     * B2C Policy XML Upload
     **********************************************************************************************/

    const handleXMLFileInput = (target: EventTarget & HTMLInputElement) => {
        
        if (target.files === null || target.files.length === 0) {
            setSelectedXMLFile(null);
        }
        else {
            setSelectedXMLFile(target.files.item(0));
        }
        target.value = '';
        
    }



    useEffect(() => {
        
        if (selectedXMLFile !== null) {
            let checkFileFormat = /(\.xml)$/i;
            if (!checkFileFormat.exec(selectedXMLFile.name)) {
                setToastContents({ header: 'XML Parse Failure', body: 'Please upload a .xml file.', severity: 'error' })
                return;
            }
            var reader = new FileReader();
            reader.onload = async function (e) {
                const fileString = e.target!.result;
                if (fileString === null)
                    return;

                let parsedPolicyData: ParsedXMLData | ErrorReturn = parseXMLPolicy(fileString as string);

                if (isErrorReturn(parsedPolicyData)) {
                    setToastContents({ header: 'XML Parse Failure', body: parsedPolicyData.error, severity: 'error' })
                    return;
                }

                
                setPolicyVariables(parsedPolicyData.policyInputs);
                setPageTemplates(parsedPolicyData.pageTemplates);
                setPageNames(Object.keys(parsedPolicyData.pageTemplates));

                setUserJournies(parsedPolicyData.userJournies);

                setSelectedXMLFile(null);
                setToastContents({ header: 'XML Parse Success', body: `Successfully parsed policy.` });
            };

            reader.onloadend = (e) => {
                if (reader.result === null) {
                    
                    setSelectedXMLFile(null);
                    setToastContents({ header: 'XML Parse Failure', body: `Unable to parse policy.`, severity: 'error' });
                }
            }
            reader.readAsText(selectedXMLFile);
        }
    }, [selectedXMLFile])

    /*********************************************************************************************
     * Suite Modifications
     **********************************************************************************************/

    const addNewSuite = (addSuiteName: string): true | string => {
        if (testSuites[addSuiteName] !== undefined) {
            return 'Error: Suite with name already exists.';
        }

        let newSuites = { ...testSuites };
        newSuites[addSuiteName] = {
            "TestConfiguration": {
                "Environment": 'Chrome',
                "TimeOut": '15',
                "OTP_Age": '15'
            },
            "Tests": []
        };

        setTestSuites({ ...newSuites });
        navigate(`/test-automation/suites/${addSuiteName}`);
        return true;
    }

    const setSuiteName = (oldTestName: string, newName: string, suiteContents: TestSuite) => {
        if (newName !== '' && newName !== oldTestName && !Object.keys(testSuites).includes(newName)) {
            let newSuites = { ...testSuites };
            newSuites[newName] = { ...suiteContents };
            delete newSuites[oldTestName];
            setTestSuites(newSuites);
            setToastContents({ header: 'Suite name successfully changed', body: `Suite name has been changed to ${newName}.` });
            navigate(`/test-automation/suites/${newName}`);
        }
        else {
            setToastContents({ header: 'Unable to save suite changes', body: `Suite name: ${newName} is invalid or a duplicate.` });
        }
    } // setTestName()

    const deleteSuite = (deleteSuiteName: string) => {
        let newSuites = { ...testSuites };

        delete newSuites[deleteSuiteName];

        setTestSuites(newSuites);
    }

    const editTestInAllSuites = (oldTestName: string, newTestName: string) => {
        let changeStateNeeded = false;
        let modifiedAllSuites = { ...testSuites }

        for (let suite of Object.keys(testSuites)) {
            if (testSuites[suite].Tests.includes(oldTestName)) {
                if (!changeStateNeeded) changeStateNeeded = true;
                let modifiedSuite = { ...testSuites[suite] };

                let indexToEdit = modifiedSuite.Tests.findIndex(testName => testName === oldTestName);
                modifiedSuite.Tests[indexToEdit] = newTestName;
                modifiedAllSuites[suite] = { ...modifiedSuite };
            }
        }

        if (changeStateNeeded) {
            setTestSuites(modifiedAllSuites);
        }
    }

    const deleteTestFromAllSuites = (removedTest: string) => {
        let changeStateNeeded = false;
        let modifiedAllSuites = { ...testSuites }

        for (let suite of Object.keys(testSuites)) {
            if (testSuites[suite].Tests.includes(removedTest)) {
                if (!changeStateNeeded) changeStateNeeded = true;
                let modifiedSuite = { ...testSuites[suite] };

                modifiedSuite.Tests = modifiedSuite.Tests.filter(testName => testName !== removedTest);
                modifiedAllSuites[suite] = { ...modifiedSuite };
            }
        }

        if (changeStateNeeded) {
            setTestSuites(modifiedAllSuites);
        }
        navigate("/test-automation/", { replace: true });
    }

    /*********************************************************************************************
     * Test Case Modifications
     **********************************************************************************************/

    const addNewTest = (newTestName: string): true | string => {
        
        let newTests = { ...tests };
        if (Object.keys(tests).includes(newTestName)) {
            return 'Error: Test with name already exists.';
        }
        newTests[newTestName] = [];
        setTests(newTests);
        navigate(`/test-automation/test-case/${newTestName}`);
        
        return true;
    } // addNewTest()

    const setTestName = (oldTestName: string, newName: string, testContents: Array<Array<strippedInputObj>>) => {
        
        if (newName !== '' && newName !== oldTestName && !Object.keys(tests).includes(newName)) {
            let newTests = { ...tests };
            newTests[newName] = [...testContents];
            delete newTests[oldTestName];
            setTests(newTests);
            setToastContents({ header: 'Suite name successfully changed', body: `Suite name has been changed to ${newName}.` });
            editTestInAllSuites(oldTestName, newName);
            navigate(`/test-automation/test-case/${newName}`);
        }
        else {
            setToastContents({ header: 'Unable to save test case changes', body: `Test case name: ${newName} is invalid or a duplicate.` });
        }
        
    } // setTestName()

    const deleteTest = (testToDelete: string) => {
        
        let newTests = { ...tests };
        delete newTests[testToDelete];

        deleteTestFromAllSuites(testToDelete);
        setTests(newTests);
        
    } // deletePage()

    return (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
            <Box sx={{ display: { xs: 'none', sm: 'unset' } }}>
                <TestSidebar tests={Object.keys(tests)} suites={Object.keys(testSuites)} setShowNewSuite={setShowNewSuite} setShowNewTest={setShowNewTest} />
            </Box>
            <Box sx={{ display: { xs: 'unset', sm: 'none' } }}><TestNavbar tests={Object.keys(tests)} suites={Object.keys(testSuites)} setShowNewSuite={setShowNewSuite} setShowNewTest={setShowNewTest} /></Box>
            <Box
                component="main"
                sx={{ flexGrow: 1, bgcolor: 'background.default', p: 3 }} className='mt-4'>
                <Routes>
                    <Route path="/suites/:suite" element={
                        <TestSuiteEditor
                            availableTests={Object.keys(tests)}
                            setPolicyVariables={setPolicyVariables}
                            setPageTemplates={setPageTemplates}
                            setPageNames={setPageNames}
                            tests={tests}
                            setTests={setTests}
                            setUserJournies={setUserJournies}
                            testSuites={testSuites}
                            setTestSuites={setTestSuites}
                            setToastContents={setToastContents}
                            deleteSuite={deleteSuite}
                            setSuiteName={setSuiteName} />}
                    />
                    <Route path="/test-case/:case" element={
                        <TestCaseEditor
                            tests={tests}
                            setTests={setTests}
                            policyVariables={policyVariables}
                            pageTemplates={pageTemplates}
                            pageNames={pageNames}
                            userJournies={userJournies}
                            downloadTestCase={downloadTestCase}
                            setToastContents={setToastContents}
                            handleXMLFileInput={handleXMLFileInput}
                            deleteTest={deleteTest}
                            setTestName={setTestName}
                        />}
                    />
                    <Route path="/" element={<></>} />
                </Routes>
            </Box>
            <AddNewTestItem showModal={showNewSuite} setShowModal={setShowNewSuite} itemType={'Suite'} createNewitem={addNewSuite} />
            <AddNewTestItem showModal={showNewTest} setShowModal={setShowNewTest} itemType={'Test'} createNewitem={addNewTest} />
            <ToastNotification showToast={showToast} setShowToast={setShowToast} toastContents={toastContents} />
        </Box>
    );
}